import { Order, OrderProduct, OrderType } from '../../../../../clients/quipli-service/order/order';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { PaymentMethodType } from '../../../../../clients/quipli-service/payment/payment';
import { OrderReturnStatus } from '../../../../../clients/quipli-service/order/order';
import { DateTime } from 'luxon';
import { OffRentRequest } from '../../../../../clients/quipli-service/order/order';
import { usePricingApiV1 } from '../../../../../clients/quipli-service/pricing/pricing-api-v1';
import { orderToCalculateFinancialSummaryRequest } from '../../shared/utils/off-rent-utils';
import { useEffect, useState } from 'react';
import { CalculateFinancialSummaryRequest } from '../../../../../clients/quipli-service/financials/financials';
import { PaymentMethodRequestV2 } from '../../../../../clients/quipli-service/payment/payment-v2';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';
import { Dictionary } from 'lodash';
import { orderToPaymentMethodV2 } from '../../status-transition-payment';
import { Invoice } from '../../../../../clients/quipli-service/invoice/invoice';
import { toast } from '../../../../../util/toasts';

export const getTimeStampForOffRentRequest = (
  onOffRentUpdateDateConfiguration: boolean,
  earliestTime: DateTime | undefined,
  orderProducts: OrderProduct[],
  offRentRequest: OffRentRequest | undefined,
  configs: Dictionary<Configuration> | undefined,
  order: Order,
) => {
  return onOffRentUpdateDateConfiguration
    ? DateTime.max(
        earliestTime ?? DateTime.fromMillis(0),
        DateTime.local({ zone: orderProducts.find((op) => op.rentalStartDate?.zone)?.rentalStartDate.zone }),
      )
    : offRentRequest?.timestamp
    ? offRentRequest.timestamp
    : DateTime.min(
        ...orderProducts.flatMap((op) =>
          op.rentalEndDate &&
          (configs && (configs['CONF_DEFAULT_RENTAL_END_TIME']?.value as 'RentalEnd' | 'CurrentTime')) === 'RentalEnd'
            ? [op.rentalEndDate]
            : order.isCycleBilled && op.type === OrderType.Rental
            ? [op.rentalStartDate.plus(order.cycleBillingTerms!.cycleLength)]
            : [],
        ),
        ...((configs && (configs['CONF_DEFAULT_RENTAL_END_TIME']?.value as 'RentalEnd' | 'CurrentTime')) ===
        'CurrentTime'
          ? [
              DateTime.local({
                zone: orderProducts.find((op) => op.rentalStartDate?.zone)?.rentalStartDate.zone,
              }),
            ]
          : []),
      );
};

export const useOffRentRequestManagement = (
  order: Order | undefined,
  onOffRentUpdateDateConfiguration: boolean,
  setExistingSummaryRequest: React.Dispatch<React.SetStateAction<CalculateFinancialSummaryRequest | undefined>>,
  configs: Dictionary<Configuration> | undefined,
) => {
  const { listPrices } = usePricingApiV1();

  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [isPricesLoading, setIsPricesLoading] = useState<boolean>(false);
  const [offRentWithPayment, setOffRentWithPayment] = useState<boolean>(true);
  const [offRentRequest, setOffRentRequest] = useState<OffRentRequest>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [customPrices, setCustomPrices] = useState<RentalProductPrice[]>();

  const computeUpdatedOrderPricing = async () => {
    setIsPricesLoading(true);
    if (order && customPrices) {
      const newPrices = await listPrices({
        productIds: order.products.filter((op) => op.type === OrderType.Rental).map((op) => op.product.id),
        locationId: order.locationId,
        orderId: order.id,
        type: OrderType.Rental,
      });

      const newCustomPrices = newPrices.flatMap((p) => {
        const rp = p as RentalProductPrice;
        if (rp.orderId) {
          return [rp];
        } else {
          return [];
        }
      });

      if (order.paymentMethod.type === PaymentMethodType.OnAccount) {
        setOffRentWithPayment(false);
      }
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && (!op.status || op.status === OrderReturnStatus.OnRent),
      );

      if (orderProducts.length > 0) {
        let earliestTime = undefined;
        for (const op of orderProducts) {
          if (!earliestTime) {
            earliestTime = op.actualRentalStartDate ?? op.rentalStartDate;
          } else {
            earliestTime = DateTime.min(earliestTime, op.actualRentalStartDate ?? op.rentalStartDate);
          }
        }

        const request = {
          offRentProducts: offRentRequest?.offRentProducts.map((orp) => orp.productId)?.length
            ? offRentRequest.offRentProducts
            : orderProducts.flatMap((op) =>
                op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent
                  ? { productId: op.id, type: op.trackingType, quantity: op.quantity ?? undefined }
                  : [],
              ),
          timestamp: getTimeStampForOffRentRequest(
            onOffRentUpdateDateConfiguration,
            earliestTime,
            orderProducts,
            offRentRequest,
            configs,
            order,
          ),
        } as OffRentRequest;
        setOffRentRequest(request);
        setExistingSummaryRequest(orderToCalculateFinancialSummaryRequest(order, newCustomPrices));
        setPaymentMethod(orderToPaymentMethodV2(order));
        setInvoice(order.invoices.find((inv) => inv));
      }
      setIsPricesLoading(false);
    }
  };

  // TODO: this should be part of the init call, doesn't need its own use effect
  useEffect(() => {
    if (order) {
      setIsPricesLoading(true);
      listPrices({
        productIds: order.products.filter((op) => op.type === OrderType.Rental).map((op) => op.product.id),
        locationId: order.locationId,
        orderId: order.id,
        type: OrderType.Rental,
      })
        .then((prices) => {
          const customPrices = prices.flatMap((p) => {
            const rp = p as RentalProductPrice;
            if (rp.orderId) {
              return [rp];
            } else {
              return [];
            }
          });
          setCustomPrices(customPrices);
        })
        .catch((e) => {
          console.log(e);
          toast('Error loading custom rental rates', 'error');
        })
        .finally(() => setIsPricesLoading(false));
    }
  }, [order]);

  useEffect(() => {
    if (invoice) {
      if (invoice.balance <= 0) {
        setOffRentWithPayment(false);
      }
    }
  }, [invoice]);

  useEffect(() => {
    computeUpdatedOrderPricing();
  }, [order, customPrices]);

  return {
    offRentRequest,
    setOffRentRequest,
    isPricesLoading,
    setIsPricesLoading,
    offRentWithPayment,
    setOffRentWithPayment,
    paymentMethod,
    setPaymentMethod,
    invoice,
    setInvoice,
    computeUpdatedOrderPricing,
    customPrices,
    setCustomPrices,
  };
};
